<template>
    <div>
        <form-wrapper :validator="$v.member" class="form-wrapper">
            <div class="custom-section-wrapper">
                <h5>Payment Information</h5>

                <div>
                    <form-group name="company" label="Company">
                        <md-input v-model="member.company" :disabled="!isAllowedToUpdate" />
                    </form-group>
                </div>

                <div class="custom-form-group">
                    <form-group name="businessRegistrationNumber" :label="businessRegistrationNumberLabel">
                        <md-input v-model="member.businessRegistrationNumber" :disabled="!isAllowedToUpdate" />
                    </form-group>
                </div>

                <div class="custom-form-group" v-if="showAcnField">
                    <form-group name="businessCompanyNumber" label="ACN">
                        <md-input v-model="member.businessCompanyNumber" :disabled="!isAllowedToUpdate" />
                    </form-group>
                </div>

                <div class="custom-form-group margin-right-0">
                    <form-group name="accountsEmail" label="Accounts Email Address">
                        <md-input v-model="member.accountsEmail" :disabled="!isAllowedToUpdate" />
                    </form-group>
                </div>

                <business-entities-dropdown v-model="member.invoicingBusinessEntityId" :disabled="!isAllowedToUpdate" />

                <div class="invoice-settings-row">
                    <div class="invoice-settings-col">
                        <form-group name="rateGroup" label="Default Service Package">
                            <md-select v-model="rateGroupId" :disabled="!isAllowedToUpdate">
                                <md-option v-for="item in rateGroups" :key="item.rateGroupId" :value="item.rateGroupId">
                                    {{ item.rateGroupName }}
                                </md-option>
                            </md-select>
                        </form-group>
                    </div>
                    <div class="invoice-settings-col">
                        <div class="skillfield-container">
                            <label
                                for="invoiceLineTypes"
                                v-if="member.invoiceLineTypes != null && member.invoiceLineTypes.length > 0"
                            >
                                Invoice Types
                            </label>
                            <multiselect
                                v-model="member.invoiceLineTypes"
                                :options="invoiceLineOptions"
                                :multiple="true"
                                :close-on-select="true"
                                placeholder="Pick invoice types"
                                label="name"
                                track-by="key"
                                :disabled="!isAllowedToUpdate"
                            ></multiselect>
                        </div>
                    </div>
                    <div class="invoice-settings-col" v-if="isNoRollUpSelected">
                        <form-group name="teamMemberInvoiceTemplateType" label="Invoice Template">
                            <md-select v-model="member.teamMemberInvoiceTemplateType" :disabled="!isAllowedToUpdate">
                                <md-option
                                    v-for="item in invoiceTemplateOptions"
                                    :key="item.key"
                                    :value="item.key"
                                >
                                    {{ item.name }}
                                </md-option>
                            </md-select>
                        </form-group>
                    </div>
                </div>
                <div class="custom-form-group custom-sp-container" v-if="rateGroupId != -1">
                    <div>
                        <label>Apply the Service Package to the following:</label>
                    </div>
                    <div>
                        <md-checkbox
                            v-model="applyRatesToValue"
                            v-for="(type, index) in applyRatesTo"
                            :key="index"
                            :value="type.key"
                            :disabled="!isAllowedToUpdate"
                        >
                            {{ type.label }}
                        </md-checkbox>
                    </div>
                </div>

                <div class="skillfield-container">
                    <multiselect
                        v-model="member.tags"
                        :options="tags"
                        :multiple="true"
                        :close-on-select="true"
                        placeholder="Tags"
                        label="name"
                        :disabled="!isAllowedToUpdate"
                        ref="tagsMultiselect"
                        track-by="tagId"
                    ></multiselect>
                </div>

                <payment-method-inputs
                    :detail="member"
                    class="custom-payment-container"
                    :disabled="!isAllowedToUpdate"
                />

                <div>
                    <form-group name="invoiceWeeklyMinimumAmount" label="Weekly Minimum (mon -> sun)">
                        <span v-if="member.invoiceWeeklyMinimumAmount" class="currency-container">
                            {{ null | currency(user.teamCurrency, true) }}
                        </span>
                        <md-input v-model.number="member.invoiceWeeklyMinimumAmount" :disabled="!isAllowedToUpdate" />
                    </form-group>
                </div>

                <div class="custom-switch">
                    <label class="label-text">
                        Apply tax for invoicing?
                    </label>
                    <md-switch v-model="member.shouldApplyTax" :disabled="!isAllowedToUpdate"></md-switch>
                </div>
            </div>

            <div class="md-layout">
                <div class="on-hold-container md-layout-item">
                    <div class="header-section">
                        <h5 class="section-title">On Hold Days</h5>
                        <md-button
                            class="md-button md-primary"
                            @click="addNewOnHoldRange"
                            :disabled="!isAllowedToUpdate"
                        >
                            <md-icon>add</md-icon>
                            Add On Hold Day
                        </md-button>
                    </div>
                    <md-table v-if="hasNoOnHoldDays" class="custom-paginated-table onhold-day-table">
                        <md-table-row slot="header">
                            <md-table-head>Reason</md-table-head>
                            <md-table-head class="start-date">Start Date</md-table-head>
                            <md-table-head class="end-date">End Date</md-table-head>
                            <md-table-head>Actions</md-table-head>
                        </md-table-row>

                        <md-table-row v-for="(onHoldDay, index) in onHoldDays" :key="index" class="row-data">
                            <md-table-cell class="reason-column">
                                <form-group name="reason" v-if="onHoldDay.inEditMode || onHoldDay.inAddMode">
                                    <md-input
                                        v-model="onHoldDay.reason"
                                        maxlength="100"
                                        :disabled="!isAllowedToUpdate"
                                    />
                                </form-group>
                                <p v-else>{{ onHoldDay.reason }}</p>
                            </md-table-cell>
                            <md-table-cell class="start-date">
                                <md-datepicker
                                    v-if="onHoldDay.inEditMode || onHoldDay.inAddMode"
                                    v-model="onHoldDay.fromDate"
                                    md-immediately
                                    :md-debounce="100"
                                    :md-model-type="String"
                                    :disabled="!isAllowedToUpdate"
                                ></md-datepicker>
                                <p v-else>{{ transformDate(onHoldDay.fromDate) }}</p>
                                <span
                                    v-if="onHoldDay.isTouched && onHoldDay.fromDate == null"
                                    class="error error-message"
                                >
                                    Start Date is required.
                                </span>
                                <span
                                    v-if="
                                        onHoldDay.isTouched &&
                                            onHoldDay.fromDate != null &&
                                            onHoldDay.toDate != null &&
                                            !$_isValidDateRange(onHoldDay.fromDate, onHoldDay.toDate)
                                    "
                                    class="error error-message"
                                >
                                    Invalid date range.
                                </span>
                            </md-table-cell>
                            <md-table-cell class="start-date">
                                <md-datepicker
                                    v-if="onHoldDay.inEditMode || onHoldDay.inAddMode"
                                    v-model="onHoldDay.toDate"
                                    md-immediately
                                    :md-debounce="100"
                                    :md-model-type="String"
                                    :disabled="!isAllowedToUpdate"
                                ></md-datepicker>
                                <p v-else-if="onHoldDay.toDate">{{ transformDate(onHoldDay.toDate) }}</p>
                            </md-table-cell>
                            <md-table-cell class="action-columns">
                                <md-button
                                    v-if="!onHoldDay.inEditMode && !onHoldDay.inAddMode"
                                    title="Edit on hold day"
                                    class="md-warning md-just-icon md-round"
                                    @click="editOnholdDay(onHoldDay)"
                                    :disabled="!isAllowedToUpdate"
                                >
                                    <md-icon>edit</md-icon>
                                </md-button>
                                <md-button
                                    v-if="onHoldDay.inEditMode || onHoldDay.inAddMode"
                                    title="Add"
                                    class="md-success md-just-icon md-round"
                                    @click="addChangesToOnHold(onHoldDay)"
                                    :disabled="!isAllowedToUpdate"
                                >
                                    <md-icon>check</md-icon>
                                </md-button>
                                <md-button
                                    v-if="!onHoldDay.inEditMode || onHoldDay.inAddMode"
                                    title="Delete on hold day"
                                    class="md-danger md-just-icon md-round"
                                    @click="deleteOnHoldDay(onHoldDay)"
                                    :disabled="!isAllowedToUpdate"
                                >
                                    <md-icon>delete</md-icon>
                                </md-button>
                                <md-button
                                    v-if="onHoldDay.inEditMode"
                                    title="Undo changes"
                                    class="md-info md-just-icon md-round"
                                    @click="undoChangesOnHoldDay(onHoldDay)"
                                    :disabled="!isAllowedToUpdate"
                                >
                                    <md-icon>restart_alt</md-icon>
                                </md-button>
                            </md-table-cell>
                        </md-table-row>
                    </md-table>
                    <div class="text-center" v-if="!hasNoOnHoldDays">
                        <p class="no-result-message">No on hold days.</p>
                    </div>
                </div>
            </div>

            <div class="custom-section-wrapper">
                <md-button
                    type="submit"
                    class="md-primary pull-right"
                    @click="validateForm"
                    :disabled="!isAllowedToUpdate"
                >
                    Save
                </md-button>
            </div>
        </form-wrapper>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import moment from 'moment';
import { email, requiredIf, maxLength } from 'vuelidate/lib/validators';
import { handleRequests, showErrorMessage, compareProperties } from '@/helpers';
import { APPLY_RATES_TO, INVOICE_LINE_TYPES, TEAM_MEMBER_INVOICE_TEMPLATE_TYPES, TEAM_MEMBER_ON_HOLD_STATUS } from '@/utils/constants';
import { resolveInvoiceLineBundleAdditions } from '@/utils/invoiceLineBundle';
import { PaymentMethodInputs, BusinessEntitiesDropdown } from '@/components';
import { PAYMENT_METHOD_DETAILS_DEFAULT, TEAM_MEMBER_ON_HOLD_DAY } from '@/utils/defaults';
import { ReverseBitwise } from '@/utils/ApplyRatesToUtils';
import { GeneralMixin } from '@/mixins/GeneralMixin';

export default {
    name: 'MemberAccountingDetails',
    components: { PaymentMethodInputs, BusinessEntitiesDropdown },
    mixins: [GeneralMixin],
    props: {
        data: {
            type: Object,
            required: true
        },
        isAllowedToUpdate: {
            type: Boolean,
            required: true
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        hasNoOnHoldDays() {
            return this.onHoldDays?.length > 0;
        },
        isNoRollUpSelected() {
            return this.member.invoiceLineTypes?.some((item) => item.key === 2) ?? false;
        },
        showAcnField() {
            return this.member.countryCode === 'AU';
        }
    },
    data() {
        return {
            member: {
                company: null,
                businessRegistrationNumber: null,
                businessCompanyNumber: null,
                accountsEmail: null,
                invoiceLineTypes: [],
                teamMemberInvoiceTemplateType: 0,
                shouldApplyTax: false,
                invoicingBusinessEntityId: null,
                paymentDetails: PAYMENT_METHOD_DETAILS_DEFAULT(),
                invoiceWeeklyMinimumAmount: null,
                onHoldDays: [],
                tags: [],
                tagsAdded: [],
                tagsDeleted: []
            },
            rateGroups: [],
            rateGroupId: -1,
            businessRegistrationNumberLabel: 'Business Registration Number',
            applyRatesTo: APPLY_RATES_TO,
            applyRatesToValue: [1, 2, 4, 16],
            invoiceLineOptions: INVOICE_LINE_TYPES,
            invoiceTemplateOptions: TEAM_MEMBER_INVOICE_TEMPLATE_TYPES,
            onHoldDays: [],
            origOnHoldDay: null,
            TEAM_MEMBER_ON_HOLD_STATUS,
            deletedOnHoldDays: [],
            tags: [],
            // True while a stored member configuration is being applied to `member`
            // (see updateMemberObject). Guards the invoice-line bundle cascade so a
            // load-time value such as [2, 4, 64] is not mistaken for a fresh user
            // toggle of "Include service lines" and does not auto-append 128 (run
            // service lines) that was never part of the stored value.
            isApplyingLoadedMember: false
        };
    },
    watch: {
        data: {
            handler(newVal) {
                this.updateMemberObject(newVal);
            },
            deep: true
        },
        'member.tags': function(newValue, oldValue) {
            if (this.$refs.tagsMultiselect) {
                const added = newValue.filter((tag) => !tag.teamMemberTagId);
                const deleted = oldValue.filter(
                    (tag) =>
                        !newValue.includes(tag) &&
                        tag.teamMemberTagId &&
                        !oldValue.includes((x) => x.teamMemberTagId) !== tag.teamMemberTagId
                );

                this.member.tagsAdded = [...added];

                if (!this.member.tagsDeleted) 
                    this.member.tagsDeleted = [];

                deleted.forEach((tag) => this.member.tagsDeleted.push(tag));
            }
        },
        'member.invoiceLineTypes': function(newValue, oldValue) {
            // Selecting "Include service lines" bundles trip + run service lines in too — invoicing teams
            // wanted one toggle that covers stops, trips, and runs. This cascade must only run on a
            // genuine user toggle: while a stored configuration is being applied on load the watcher
            // sees oldValue as the empty data() default, which would otherwise look like a fresh toggle
            // and wrongly append run service lines (128) — see isApplyingLoadedMember.
            if (!this.isApplyingLoadedMember) {
                const missing = resolveInvoiceLineBundleAdditions(newValue, oldValue);
                if (missing.length > 0) {
                    this.member.invoiceLineTypes = [...(newValue || []), ...missing];
                    return;
                }
            }

            const lineOptions = [...INVOICE_LINE_TYPES];
            if (newValue?.length <= 0 || newValue === null) {
                this.invoiceLineOptions = lineOptions;
            } else {
                const selectedKeys = newValue.map((item) => item.key);

                this.invoiceLineOptions = lineOptions.filter(
                    (line) => !newValue.some((value) => line.inCompatible.includes(value.key))
                );
                this.invoiceLineOptions = this.invoiceLineOptions.filter((item) => !selectedKeys.includes(item.key));
            }

            // Clear invoice template type when 'No roll up' is deselected
            const hasNoRollUp = newValue?.some((item) => item.key === 2) ?? false;
            if (!hasNoRollUp) {
                this.member.teamMemberInvoiceTemplateType = 0;
            }
        }
    },
    async mounted() {
        if (this.rateGroups.length !== 0) {
            return;
        }

        const list = await this.getRateGroupsList();
        if (list && list.length) 
            this.rateGroups = [...list];

        this.oldPaymentMethod = this.data.paymentDetails.paymentMethod;

        this.updateMemberObject(this.data);

        await this.getTags();
    },
    validations: {
        member: {
            accountsEmail: {
                email
            },
            paymentDetails: {
                bank: {
                    name: {
                        required:
                            // eslint-disable-next-line func-names
                            requiredIf(function() {
                                return (
                                    this.member.paymentDetails.paymentMethod === 'Bank Transfer' &&
                                    !this.member.paymentDetails.bank.name
                                );
                            })
                    },
                    accountNumber: {
                        required:
                            // eslint-disable-next-line func-names
                            requiredIf(function() {
                                return (
                                    this.member.paymentDetails.paymentMethod === 'Bank Transfer' &&
                                    !this.member.paymentDetails.bank.accountNumber
                                );
                            })
                    },
                    bsb: {
                        required:
                            // eslint-disable-next-line func-names
                            requiredIf(function() {
                                return (
                                    this.member.paymentDetails.paymentMethod === 'Bank Transfer' &&
                                    !this.member.paymentDetails.bank.bsb
                                );
                            })
                    }
                }
            },
            invoiceWeeklyMinimumAmount: {
                maxLength: maxLength(10)
            }
        }
    },
    methods: {
        hasChanges() {
            // since we dont store the rateGroupId value in the this.member object we need to check it separately
            const rateGroupId = this.rateGroupId === -1 ? null : this.rateGroupId;

            if (this.data.rateGroupId !== rateGroupId) {
                return true;
            }

            if (this.data.applyRatesTo !== this.applyRatesToValue.reduce((a, b) => a + b, 0)) {
                return true;
            }

            if (this.oldPaymentMethod !== this.member.paymentDetails.paymentMethod) {
                return true;
            }

            const noChanges = compareProperties(this.member, this.data, [
                'company',
                'businessRegistrationNumber',
                'businessCompanyNumber',
                'accountsEmail',
                'invoicingBusinessEntityId',
                'rateGroupId',
                'invoiceLineTypes',
                'teamMemberInvoiceTemplateType',
                'shouldApplyTax',
                'invoiceWeeklyMinimumAmount'
            ]);

            if (!noChanges) {
                return true;
            }

            const noNestedPropertyChanges = compareProperties(this.member.paymentDetails, this.data.paymentDetails, [
                'paymentMethod',
                'bank'
            ]);

            if (!noNestedPropertyChanges) {
                return true;
            }

            const noChangesInPaymentDetails = compareProperties(
                this.member.paymentDetails.bank,
                this.data.paymentDetails.bank,
                ['name', 'accountNumber', 'bsb', 'remittance']
            );

            return !noChangesInPaymentDetails;
        },
        updateMemberObject(details) {
            // Suppress the invoice-line bundle cascade while the stored value is applied, so loading a
            // member does not auto-select run service lines (128) that were never part of the saved
            // configuration. Reset on the next tick, once Vue has flushed the member.invoiceLineTypes
            // watcher triggered by this assignment, so genuine user toggles still cascade.
            this.isApplyingLoadedMember = true;

            this.member = _.cloneDeep(details);
            this.member.paymentDetails = _.cloneDeep(details.paymentDetails || PAYMENT_METHOD_DETAILS_DEFAULT());
            this.member.teamMemberInvoiceTemplateType = details.teamMemberInvoiceTemplateType || 0;

            this.rateGroupId = details.rateGroupId || -1;
            this.applyRatesToValue = ReverseBitwise(this.member.applyRatesTo, APPLY_RATES_TO);
            this.setBusinessRegistrationNumberLabel(this.member.countryCode);
            this.onHoldDays = _.cloneDeep(this.data.onHoldDays);

            this.$nextTick(() => {
                this.isApplyingLoadedMember = false;
            });
        },
        validateForm() {
            this.$v.$touch();

            if (this.onHoldDays && this.onHoldDays.some((day) => day.isTouched === true)) {
                showErrorMessage(this, 'Please finish off on hold days', null);
                return;
            }

            if (this.onHoldDays && this.onHoldDays.some((day) => day.inEditMode || day.inAddMode)) {
                this.onHoldDays = this.onHoldDays.filter((day) => !(day.inEditMode || day.inAddMode));
            }

            if (!this.$v.$invalid) {
                this.update();
            }
        },
        update() {
            this.member.applyRatesTo = this.applyRatesToValue.reduce((a, b) => a + b, 0);

            this.member.rateGroupId = this.rateGroupId !== -1 ? this.rateGroupId : null;
            this.member.teamMemberInvoiceTemplateType = this.member.teamMemberInvoiceTemplateType || null;
            this.oldPaymentMethod = this.member.paymentDetails.paymentMethod;
            this.member.onHoldDays = this.onHoldDays;

            this.member.addedOnHoldDays = this.onHoldDays?.filter((onHold) => onHold.isNew) ?? [];
            this.member.updatedOnHoldDays = this.onHoldDays?.filter((onHold) => onHold.isUpdated) ?? [];
            this.member.deletedOnHoldDays = this.deletedOnHoldDays ?? [];

            this.$emit('updated', this.member);
        },
        setBusinessRegistrationNumberLabel(countryCode) {
            switch (countryCode) {
                case 'AU':
                    this.businessRegistrationNumberLabel = 'ABN';
                    break;
                case 'NZ':
                    this.businessRegistrationNumberLabel = 'NZBN';
                    break;
                default:
                    this.businessRegistrationNumberLabel = 'Business Registration Number';
                    break;
            }
        },
        async getRateGroupsList() {
            let response = {
                data: []
            };
            try {
                this.isListLoading = true;
                const endpoint = `/api/rate-groups`;

                response = await handleRequests(endpoint);
            } catch (error) {
                const message = 'Error in getting the rate groups list';
                showErrorMessage(this, message, error);
            }
            this.isListLoading = false;

            let rateGroups = response.data.map((rg) => {
                return {
                    rateGroupId: rg.rateGroupId,
                    rateGroupName: rg.rateGroupName
                };
            });

            rateGroups = [
                {
                    rateGroupId: -1,
                    rateGroupName: 'Default'
                },
                ...rateGroups
            ];

            return rateGroups;
        },
        addNewOnHoldRange() {
            const onHold = Object.assign({}, TEAM_MEMBER_ON_HOLD_DAY);
            onHold.userId = this.data.userId;
            onHold.onHoldStatusId = TEAM_MEMBER_ON_HOLD_STATUS.Invoice;
            this.onHoldDays.unshift(onHold);
        },
        editOnholdDay(onHoldDay) {
            this.origOnHoldDay = _.cloneDeep(onHoldDay);

            const idx = this.onHoldDays.indexOf(onHoldDay);
            if (idx !== -1) {
                const updated = {
                    ...onHoldDay,
                    inEditMode: true,
                    inAddMode: false,
                    isTouched: false
                };

                this.$set(this.onHoldDays, idx, updated);
            }
        },
        addChangesToOnHold(onHoldDay) {
            if (!onHoldDay) 
                return;

            // If both fromDate and toDate are present, validate the range
            if (
                !onHoldDay.fromDate ||
                (onHoldDay.fromDate &&
                    onHoldDay.toDate &&
                    !this.$_isValidDateRange(onHoldDay.fromDate, onHoldDay.toDate))
            ) {
                onHoldDay.isTouched = true;
                return;
            }

            if (onHoldDay.inAddMode) {
                onHoldDay.inAddMode = false;
            } else if (onHoldDay.inEditMode) {
                onHoldDay.inEditMode = false;
            }

            onHoldDay.isTouched = false;
            onHoldDay.hasChanged = true;

            if (!onHoldDay.isNew) {
                onHoldDay.isUpdated = true;
            }

            this.origOnHoldDay = null;
        },
        deleteOnHoldDay(onHoldDay) {
            const idx = this.onHoldDays.indexOf(onHoldDay);

            if (idx !== -1) {
                const updated = {
                    ...onHoldDay,
                    isDeleted: true
                };

                if (!updated.isNew) {
                    this.deletedOnHoldDays.push(updated);
                }

                this.onHoldDays.splice(idx, 1);
            }
        },
        undoChangesOnHoldDay(onHoldDay) {
            if (!onHoldDay || !onHoldDay.inEditMode || !this.origOnHoldDay) 
                return;

            // Revert to original values
            onHoldDay.fromDate = this.origOnHoldDay.fromDate;
            onHoldDay.toDate = this.origOnHoldDay.toDate;
            onHoldDay.inEditMode = false;
            onHoldDay.reason = this.origOnHoldDay.reason;
            this.origOnHoldDay = null;
        },
        isNotValidDate(startDate, endDate) {
            return !startDate || !endDate || !this.$_isValidDateRange(startDate, endDate);
        },
        transformDate(date) {
            return moment(date).format('MMM D, YYYY');
        },
        async getTags() {
            const api = `/api/teams/settings/tags?showDeletedTags=false`;
            try {
                const response = await handleRequests(api);
                if (response?.data) 
                    this.tags = [...response.data];
            } catch (error) {
                const message = 'Error in getting tags';
                showErrorMessage(this, message, null);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.form-wrapper {
    padding: 0 15px;
}
.custom-section-wrapper {
    h5 {
        font-weight: 500;
        text-transform: uppercase;
        font-size: 12px;
    }

    .custom-form-group {
        width: calc((100% - 30px) / 2);
        display: inline-block;
        margin-right: 30px;
        vertical-align: top;
    }

    .margin-right-0 {
        margin-right: 0;
    }

    .date-form-group {
        width: 250px;
    }
}

.custom-section-wrapper:last-child {
    border: none;
}

.custom-payment-container {
    margin-top: 10px;
    ::v-deep .md-layout:first-child .md-layout-item {
        padding: 0;
    }

    ::v-deep .md-layout:nth-child(2) {
        .md-layout-item:first-child {
            padding-left: 0;
        }
        .md-layout-item:nth-child(3) {
            padding-right: 0;
        }
    }
}

.custom-switch {
    margin-top: 22px;
    margin-bottom: 0px;

    label {
        float: left;
        font-weight: 400;
        color: #aaaaaa !important;
        line-height: 1.4;
    }

    .md-checked {
        ::v-deep .md-switch-container {
            background-color: #2b93ff !important;
        }

        ::v-deep .md-switch-thumb {
            border: 1px solid #2b93ff !important;
        }
    }

    ::v-deep .md-switch-label {
        margin-top: 0px;
    }

    .md-switch {
        margin: 0;
        margin-bottom: 10px;
        padding-left: 10px;
    }
}

.custom-sp-container {
    margin-right: 0 !important;
    position: relative;
    label {
        top: -4px;
        font-size: 0.6875rem;
        color: #aaa;
        font-weight: 400;
        position: absolute;
    }

    ::v-deep .md-checkbox {
        margin-bottom: 0px;
        margin-top: 27px;
    }
}

.invoice-settings-row {
    display: flex;
    gap: 30px;
    align-items: flex-end;
}

.invoice-settings-col {
    flex: 1;
    min-width: 0;
}

.skillfield-container {
    margin-right: 0 !important;
    label {
        font-size: 0.6875rem;
        color: #aaa;
        font-weight: 400;
    }
}

.currency-container {
    padding-top: 7px;
    margin-right: 4px;
    cursor: text !important;
}

.on-hold-container {
    padding-top: 30px;
    height: auto !important;
    max-width: 800px !important;

    .header-section {
        width: 100%;
        height: 48px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .row-data {
        max-height: 83px !important;
    }

    .selected-row {
        background-color: #f5f5f5 !important;
        height: 100px;
    }

    .start-date,
    .end-date {
        width: 30% !important;
        max-width: 10px;
        padding-bottom: 20px !important;
    }

    .reason-column {
        width: 30% !important;
        max-width: 100px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-bottom: 20px !important;
    }

    .action-columns {
        text-align: center;
        padding-bottom: 20px !important;
        .md-button:first-child {
            margin-right: 15px;
        }
    }

    .section-title {
        margin: 0;
        font-weight: 500;
    }

    .no-result-message {
        margin: 16px 0;
        font-size: medium;
        font-style: italic;
    }
}

.onhold-day-table .md-table-row:nth-child(n + 3) > td {
    border-top: 0 !important;
}

// .md-split-field {
//     display: inline-block;
//     width: 45%;
//     margin-right: 0 !important
// }
</style>
